import {
  stumble,
  stumbling,
} from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type AdvancedFilterTableColumnDefinition,
  type AttributeFilter,
  type Filter,
  type FilterValue,
  type Filters,
  NAME_CONTAINS_ANY_OPERATOR,
  NAME_NOT_CONTAINS_ANY_OPERATOR,
  type TableFilter,
  type TableFilterOperator,
  createTextFilter,
  getFilterTupleOperand,
  getFilterTupleOperator,
  isArrayContainsOperator,
  isDateTimeTableFilter,
  isLabelFilterArray,
  isLabelsArrayOperator,
  isLabelsTableFilter,
  isNameOnlyLabelFilter,
  isNonNullable,
  isObjectTableFilter,
  labelFilterToDisplayToken,
  narrowType,
  serializeArrayContainsFilterValue,
} from '@ipfabric/utils';
import { is } from '@ipfabric/utils/fp/comparison';
import { prop } from '@ipfabric/utils/fp/prop';
import { format } from 'date-fns';
import { string } from 'fp-ts';
import { flow } from 'fp-ts/lib/function';
import { formatFilterValue } from '../SimpleFilter/helpers/formatFilterValue';
import {
  type GroupFilter,
  type RuleFilter,
  emptyGroupFilter,
  emptyRuleFilter,
} from './useAdvancedFilter';
import type { RuleContext } from './useRuleContext';

const getValueFromFilter = (
  filter: Filter,
  columnFilter: TableFilter | undefined,
  ruleContext: RuleContext,
): string => {
  const value = filter[filter.length - 1];
  if (isLabelFilterArray(value)) {
    return labelFilterToDisplayToken(value[0]!);
  }
  if (Array.isArray(value)) {
    return serializeArrayContainsFilterValue(
      value.filter((item): item is string => typeof item === 'string'),
    );
  }
  // if first field of filter is operator 'now', then values is number of days
  if (
    typeof value === 'number' &&
    isDateTimeTableFilter(columnFilter) &&
    filter[0] !== 'now'
  ) {
    const time = new Date(value);
    const startOfTheDay =
      time.getUTCSeconds() === 0 &&
      time.getUTCMinutes() === 0 &&
      time.getUTCHours() === 0;
    if (startOfTheDay) {
      return format(value, ruleContext.dateFormat);
    }
    return format(value, ruleContext.dateTimeFormat);
  }
  return value?.toString() ?? '';
};

const isArrayOperator = (x: unknown): x is 'all' | 'any' =>
  ['any', 'all'].includes(x as string);

const getObjectChildKeyFromFilter = (filter: Filter): string => {
  const keyOrColumnOperator = filter[filter.length - 3];
  const key =
    keyOrColumnOperator === 'column'
      ? filter[filter.length - 4]
      : keyOrColumnOperator;
  return narrowType(string.isString)(key);
};

const getLabelsNameOnlyOperatorAndValue = (
  filter: Filter,
  filterDefinition: TableFilter,
  operator: FilterValue | FilterValue[],
): { operator: TableFilterOperator; value: string } | undefined => {
  if (
    !isLabelsTableFilter(filterDefinition) ||
    (operator !== 'containsAny' && operator !== 'notContainsAny')
  ) {
    return undefined;
  }

  const rawValue = filter[filter.length - 1];
  if (!isLabelFilterArray(rawValue)) {
    return undefined;
  }

  const labelFilter = rawValue[0];
  if (!labelFilter || !isNameOnlyLabelFilter(labelFilter)) {
    return undefined;
  }

  return {
    operator: (operator === 'notContainsAny'
      ? NAME_NOT_CONTAINS_ANY_OPERATOR
      : NAME_CONTAINS_ANY_OPERATOR) as TableFilterOperator,
    value: labelFilter.name,
  };
};

/**
 * This method is probably the best source of truth how to parse `Filter` type.
 */
export const getRuleFilter = <TRow,>(
  filter: Filter,
  column: AdvancedFilterTableColumnDefinition<TRow> | undefined,
  ruleContext: RuleContext,
): RuleFilter | null => {
  let dynamicField = '';
  let processedFilter = filter;
  if (column?.key === 'device.attributes') {
    // Until there is only one kind of such key, keep hardcoded.
    [dynamicField, ...processedFilter] = processedFilter as AttributeFilter;
  }

  const value = getValueFromFilter(filter, column?.filter, ruleContext);

  const isOperatorValidEmpty = (operator: FilterValue | FilterValue[]) =>
    operator === 'empty' && (value === 'false' || value === 'true');

  const isOperatorValidNow = (operator: FilterValue | FilterValue[]) =>
    operator === 'now' &&
    (getFilterTupleOperand(filter, 2) === 'lt' ||
      getFilterTupleOperand(filter, 2) === 'gt');

  const isColumnOperator = (operator?: FilterValue | FilterValue[]) =>
    isNonNullable(operator) && operator === 'column';

  const isUnsupportedCrossColumnOperator = (
    operator: FilterValue | FilterValue[],
  ) =>
    operator === 'column' &&
    filter[filter.length - 2] !== 'eq' &&
    filter[filter.length - 2] !== 'neq';

  const handleColumnOperator = (
    currentFilter: Filter,
    operator: FilterValue | FilterValue[],
  ) => {
    const ternaryOperator = getFilterTupleOperand(currentFilter, 3);
    if (isColumnOperator(ternaryOperator)) {
      // Already checked inside `isColumnOperator` if `ternaryOperator` is not nullable
      if (isUnsupportedCrossColumnOperator(ternaryOperator!)) {
        return {
          operator: null as any, // Mark as invalid
          value,
        };
      }

      return {
        operator: operator === 'neq' ? 'ncolumn' : 'column',
        value,
      };
    }

    return undefined;
  };

  const getOperatorAndValue = (
    filterDefinitionForValue: TableFilter,
  ): {
    operator: TableFilterOperator;
    value: string;
  } => {
    let operator: FilterValue | FilterValue[];
    if (filter[0] === 'color') {
      // This operator has form ['color', 'eq', ...], i.e. stretched to two
      // elements, making the logic for object and array break and mistakenly detect 'eq'.
      // This is why color is handled separately.
      // https://gitlab.com/ip-fabric/development/product/ipfabric-product/-/merge_requests/3751#note_1920939366
      operator = 'color';
    } else {
      operator = getFilterTupleOperator(filter);
    }
    if (isOperatorValidEmpty(operator)) {
      return {
        operator:
          value === 'true'
            ? 'emptyTrue'
            : ('emptyFalse' as TableFilterOperator),
        value: '',
      };
    }
    // Fixed noUncheckedIndexedAccess NIM-19408
    if (isOperatorValidNow(getFilterTupleOperand(filter, 3)!)) {
      // if operator is "now" then second field of filter is operator "lt" or "gt"
      return {
        operator:
          filter[1] === 'gt' ? 'nowGt' : ('nowLt' as TableFilterOperator),
        value,
      };
    }
    const columnOperator = handleColumnOperator(filter, operator);
    if (columnOperator) {
      return columnOperator;
    }
    if (operator === 'color') {
      // if operator is "color" the value is not value belonged to key but severity code
      return {
        operator,
        value,
      };
    }

    const labelsOperatorAndValue = getLabelsNameOnlyOperatorAndValue(
      filter,
      filterDefinitionForValue,
      operator,
    );
    if (labelsOperatorAndValue) {
      return labelsOperatorAndValue;
    }

    return {
      operator: operator as TableFilterOperator,
      value: formatFilterValue(value, filterDefinitionForValue),
    };
  };

  const getFilterForObject = (): TableFilter => {
    if (column?.filter && isObjectTableFilter(column.filter)) {
      if (isTopLevelOperator) {
        return column?.filter;
      }
      const childField = column.filter.children.find(
        flow(prop('field'), is(getObjectChildKeyFromFilter(filter))),
      );
      if (childField) {
        return childField.filter;
      }
    }
    return stumbling(
      'child filter not found; falling back to text filter to satisfy types',
    )(createTextFilter());
  };

  // In case of object column, guess from the filter whether
  // we are filtering by the parent or child field.
  // I know this is a hack, and will be for now, because
  // there is no deterministic way of parsing filters consistent
  // between backend and frontend. Properly fixing this
  // would deserve an abstraction layer to parse Filters, properly determining
  // whether we filter using the parent or child field.
  // see NIM-14783
  const isTopLevelOperator =
    isOperatorValidEmpty(filter[0]) ||
    filter[0] === 'color' ||
    isArrayContainsOperator(filter[0] as string) ||
    isLabelsArrayOperator(filter[0] as string);

  if (column?.filter) {
    const resultColumn =
      isObjectTableFilter(column.filter) && !isTopLevelOperator
        ? {
            key:
              filter[0] === 'empty'
                ? column.key
                : getObjectChildKeyFromFilter(filter),
            parent: column.key,
            filter: getFilterForObject(),
          }
        : {
            key: column.key,
            filter: column.filter,
          };

    const operatorAndValue = getOperatorAndValue(resultColumn.filter);
    const hasUnsupportedOperator = operatorAndValue.operator === null;

    return {
      type: 'ruleFilter',
      column: resultColumn,
      dynamicField,
      ...operatorAndValue,
      unsupportedOperatorError: hasUnsupportedOperator
        ? 'This filter uses an unsupported cross-column operator that is no longer supported. Please update the filter to use supported operators.'
        : undefined,
      arrayOperator:
        column.array && !isTopLevelOperator
          ? // casting since we asume filter is valid and therefore it's any or all
            narrowType(isArrayOperator)(filter[0])
          : undefined,
    };
  }
  return null;
};

const getChildFilter = <TRow,>(
  filter: Filters[] | undefined,
  columns: AdvancedFilterTableColumnDefinition<TRow>[],
  ruleContext: RuleContext,
): (GroupFilter | RuleFilter)[] => {
  const parsedFilter = filter
    ? filter.reduce<(GroupFilter | RuleFilter)[]>((acc, f) => {
        const keys = Object.keys(f);

        for (const key of keys) {
          if (key === 'and' || key === 'or') {
            acc.push({
              type: 'groupFilter',
              operator: key,
              filters: getChildFilter((f as any)[key], columns, ruleContext),
            });
          } else {
            const ruleFilter = getRuleFilter(
              (f as any)[key],
              columns.find((col) => col.key === key),
              ruleContext,
            );

            if (ruleFilter) {
              acc.push(ruleFilter);
            }
          }
        }

        return acc;
      }, [])
    : [];
  return parsedFilter.length === 0 ? [emptyRuleFilter] : parsedFilter;
};

export const getFromFilters = <TRow,>(
  filter: Filters,
  columns: AdvancedFilterTableColumnDefinition<TRow>[],
  ruleContext: RuleContext,
): GroupFilter => {
  const keys = Object.keys(filter);

  if (keys.length === 1) {
    const operator = keys[0];

    if (operator !== 'and' && operator !== 'or') {
      stumble('Invalid initial advanced filter');

      return emptyGroupFilter;
    }

    return {
      type: 'groupFilter',
      operator,
      filters: getChildFilter(
        filter[operator] as Filters[],
        columns,
        ruleContext,
      ),
    };
  }
  const advancedFilter = keys.reduce<Filters[]>((acc, key) => {
    acc.push({ [key]: (filter as any)[key] });

    return acc;
  }, []);

  return {
    type: 'groupFilter',
    operator: 'and',
    filters: getChildFilter(advancedFilter, columns, ruleContext),
  };
};
